import { Component, computed, effect, input, model, signal } from '@angular/core';
import { Currency, ICashRegisterBalance } from '@vierkant-software/types__api';
import { Wrapper } from '../../modules/wrappers';
import { CashRegisterBalanceConfig } from './v-cash-register-balance.config';

interface DenominationConfig {
  coins: number[];
  bills: number[];
}

@Component({
  selector:    'v-cash-register-balance',
  templateUrl: './v-cash-register-balance.component.haml',
  styleUrls:   ['./v-cash-register-balance.component.sass'],
  standalone:  false,
})
export class VCashRegisterBalanceComponent extends Wrapper<CashRegisterBalanceConfig> {
  protected _baseConfig: CashRegisterBalanceConfig = CashRegisterBalanceConfig.baseTheme.Default;

  balance = model.required<ICashRegisterBalance>();
  minTotal = input<number | undefined>(undefined);
  maxTotal = input<number | undefined>(undefined);
  required = input<boolean>(false);
  label = input<string | undefined>(undefined);
  totalLabel = input<string | undefined>(undefined);

  private coinsState = signal<Record<string, number>>({});
  private billsState = signal<Record<string, number>>({});
  private touchedState = signal<boolean>(false);

  hasError = signal<boolean>(false);
  errorMessage = signal<string>('');

  get currency(): Currency {
    const configValue = this.config();
    if (typeof configValue === 'string')
      return configValue as Currency;

    return configValue?.currency || Currency.EUR;
  }

  private readonly denominationConfigs: Record<Currency, DenominationConfig> = {
    [Currency.EUR]: {
      coins: [0.01, 0.02, 0.05, 0.10, 0.20, 0.50, 1, 2],
      bills: [5, 10, 20, 50, 100, 200, 500]
    },
    [Currency.eur]: {
      coins: [0.01, 0.02, 0.05, 0.10, 0.20, 0.50, 1, 2],
      bills: [5, 10, 20, 50, 100, 200, 500]
    },
    [Currency.CHF]: {
      coins: [0.05, 0.10, 0.20, 0.50, 1, 2, 5],
      bills: [10, 20, 50, 100, 200, 1000]
    }
  };

  // minor units, like every amount of the backend
  totalAmount = computed(() => {
    return this.getTotalCoinsValue() + this.getTotalBillsValue();
  });

  private initialized = signal(false);

  constructor() {
    super();
    effect(() => {
      const inputBalance = this.balance();
      if (inputBalance && !this.initialized()) {
        this.initializeFromBalance(inputBalance);
        this.initialized.set(true);
      }
    });

    effect(() => {
      this.coinsState();
      this.billsState();

      const updatedBalance = this.buildBalanceObject();
      this.balance.set(updatedBalance);
    });
  }

  getDenominations(): DenominationConfig {
    return this.denominationConfigs[this.currency];
  }

  updateCoinAmount(amount: number, coinValue: number) {
    const key = this.getCoinKey(coinValue);
    const validAmount = Math.max(0, Math.floor(amount || 0));
    this.coinsState.update(coins => ({
      ...coins,
      [key]: validAmount
    }));
    this.touchedState.set(true);
  }

  updateBillAmount(amount: number, billValue: number) {
    const key = billValue.toString();
    const validAmount = Math.max(0, Math.floor(amount || 0));
    this.billsState.update(bills => ({
      ...bills,
      [key]: validAmount
    }));
    this.touchedState.set(true);
  }

  onTouchedWrapper() {
    this.touchedState.set(true);
  }

  getCoinAmount(coinValue: number): number {
    const key = this.getCoinKey(coinValue);
    return this.coinsState()[key] ?? 0;
  }

  getBillAmount(billValue: number): number {
    const key = billValue.toString();
    return this.billsState()[key] ?? 0;
  }

  calculateCoinValue(coinValue: number): number {
    return this.toMinorUnits(coinValue) * this.getCoinAmount(coinValue);
  }

  calculateBillValue(billValue: number): number {
    return this.toMinorUnits(billValue) * this.getBillAmount(billValue);
  }

  getTotalCoinsValue(): number {
    return this.getDenominations().coins.reduce((total, coinValue) => {
      return total + this.calculateCoinValue(coinValue);
    }, 0);
  }

  getTotalBillsValue(): number {
    return this.getDenominations().bills.reduce((total, billValue) => {
      return total + this.calculateBillValue(billValue);
    }, 0);
  }

  getGrandTotal(): number {
    return this.totalAmount();
  }

  getCoinKey(coinValue: number): string {
    // Convert decimal coins to string keys (e.g., 0.01 -> "001", 0.10 -> "010")
    if (coinValue < 1)
      return (coinValue * 100).toString().padStart(3, '0');

    return coinValue.toString();
  }

  formatDenomination(value: number): string {
    const currency = this.currency;

    if (value < 1) {
      const cents = Math.round(value * 100);
      return `${this.formatNumber(cents, true)} Cent`;
    } else {
      return `${this.formatNumber(value, true)} ${currency === Currency.CHF ? 'CHF' : 'Euro'}`;
    }
  }

  private formatNumber(value: number, noDigits: boolean = false): string {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: noDigits ? 0 : 2,
      maximumFractionDigits: noDigits ? 0 : 2
    }).format(value);
  }

  private initializeFromBalance(balance: ICashRegisterBalance) {
    const coinsRecord: Record<string, number> = {};
    Object.entries(balance.coins || {}).forEach(([key, value]: [string, {amount: number}]) => {
      coinsRecord[key] = typeof value === 'object' ? value.amount : value;
    });
    this.coinsState.set(coinsRecord);

    const billsRecord: Record<string, number> = {};
    Object.entries(balance.bills || {}).forEach(([key, value]: [string, {amount: number}]) => {
      billsRecord[key] = typeof value === 'object' ? value.amount : value;
    });
    this.billsState.set(billsRecord);
  }

  private buildBalanceObject(): ICashRegisterBalance {
    const coinsObj: Record<string, {amount: number}> = {};
    Object.entries(this.coinsState()).forEach(([key, amount]) => {
      coinsObj[key] = { amount };
    });

    const billsObj: Record<string, {amount: number}> = {};
    Object.entries(this.billsState()).forEach(([key, amount]) => {
      billsObj[key] = { amount };
    });

    return {
      coins:       coinsObj,
      bills:       billsObj,
      currency:    this.currency,
      totalAmount: this.totalAmount()
    } as ICashRegisterBalance;
  }

  // the denominations are in the main unit; rounded once, so every sum stays an integer
  private toMinorUnits(denomination: number): number {
    return Math.round(denomination * 100);
  }
}
